import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { AudioRecordingSection } from '@components/AudioRecordingSection';
import { useDialog } from '@contexts/DialogContext';
import { useSnackbar } from '@contexts/SnackbarContext';
import { useVoiceRecordings } from '@contexts/VoiceRecordingsContext';
import { Box, Button, Typography } from '@mui/material';
import { voiceRecordingsService } from '@services/voice-recordings.service';
import { isAppApiError } from '@utils/service.utils';
import { useCallback, useMemo, useState } from 'react';
export const VoiceRecordingList = () => {
    const { remoteRecordings, clearAllRemoteRecordings } = useVoiceRecordings();
    const [isDeleting, setIsDeleting] = useState(false);
    const { showSnackbar } = useSnackbar();
    const { showDialog, showConfirmDialog } = useDialog();
    const { consentAudioUrl, referenceAudioUrl, languageKey } = useMemo(() => {
        const languageKeys = Object.keys(remoteRecordings);
        if (languageKeys.length === 0) {
            return { consentAudioUrl: '', referenceAudioUrl: '' };
        }
        const firstLanguageRecordings = remoteRecordings[languageKeys[0]];
        return {
            consentAudioUrl: firstLanguageRecordings?.consentAudio?.url ?? '',
            referenceAudioUrl: firstLanguageRecordings?.referenceAudio?.url ?? '',
            languageKey: languageKeys[0],
        };
    }, [remoteRecordings]);
    const confirmDelete = () => {
        showConfirmDialog('Are you sure you want to delete these voice recordings?', handleDeleteRecordings);
    };
    const handleDeleteRecordings = useCallback(async () => {
        if (!languageKey) {
            showSnackbar('Language key not found', 'error');
            return;
        }
        try {
            setIsDeleting(true);
            await voiceRecordingsService.deleteRecordings(languageKey);
            showDialog('Voice recordings deleted successfully!', {
                onClose: () => {
                    // Clear remote recordings for this language after successful delete
                    clearAllRemoteRecordings();
                },
            });
        }
        catch (error) {
            const errorMessage = isAppApiError(error)
                ? error.message
                : 'Failed to upload recordings. Please try again.';
            showSnackbar(String(errorMessage), 'error');
        }
        finally {
            setIsDeleting(false);
        }
    }, [clearAllRemoteRecordings, showSnackbar, showDialog, languageKey]);
    return (_jsxs(_Fragment, { children: [_jsxs(Box, { children: [_jsx(Typography, { variant: "h1", color: "primary", children: "Your Voice Recordings" }), _jsx(Typography, { variant: "caption", color: "text.secondary", children: "Manage and listen to your voice pack recordings" })] }), _jsx(AudioRecordingSection, { enabled: true, sectionNumber: 1, sectionTitle: "Consent", recordingUrl: consentAudioUrl }), _jsx(AudioRecordingSection, { enabled: true, sectionNumber: 2, sectionTitle: "Sample Voice Recording", recordingUrl: referenceAudioUrl }), _jsx(Button, { disabled: isDeleting, variant: "outlined", color: "error", sx: { alignSelf: 'flex-end', width: 'fit-content' }, onClick: confirmDelete, children: "Delete" })] }));
};
